import * as React from 'react';
import type { UserFlow } from '../../../MedicareProAPI/Entities/User/UserFlow';
import type { UserScope } from '../../../MedicareProAPI/Entities/User/User';
import { ICON_GROUPS, ICONS_FLAT } from '../../Constants/Icons';
import type { StageVM, FlowBundlePayload } from '../../../MedicareProAPI/Entities/User/UserFlow';

/* ───────── Types ───────── */
type FlowKanbanEditorProps = {
  userFlow?: UserFlow;
  initialFlowName?: string;
  initialStages?: StageVM[];
  colorOptions: string[];
  colorHexMap?: Record<string, string>;
  iconPrefix?: 'fal' | 'far' | 'fas';
  /** onUpsert now receives the single-transaction bundle */
  onUpsert: (payload: FlowBundlePayload) => void | Promise<void>;
  onCancel?: () => void;
  onDeleteFlow?: (flowId: number) => void;
  mode?: string;
  userScope: UserScope;
};

/* ───────── Small utilities ───────── */
const rid = () => (crypto as any)?.randomUUID?.() ?? Math.random().toString(36).slice(2);
const useOutsideClose = () => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  return { open, setOpen, ref };
};

/* ───────── Popout container ───────── */
function Popout({
  trigger,
  children,
  align = 'right',
}: {
  trigger: (args: { open: boolean; setOpen: (o: boolean) => void }) => React.ReactNode;
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  const { open, setOpen, ref } = useOutsideClose();
  return (
    <div ref={ref} className="d-inline-block position-relative">
      <span onClick={() => setOpen(!open)} className="cp d-inline-flex align-items-center">
        {trigger({ open, setOpen })}
      </span>
      {open && (
        <div
          className={`popout shadow-sm border bg-white rounded p-1 ${align === 'right' ? 'popout-right' : 'popout-left'}`}
          role="menu"
        >
          {children}
        </div>
      )}
    </div>
  );
}

/* ───────── Visual pickers ───────── */
// Color picker: solid square tiles (no text)
function ColorSwatchPicker({ value, options, onChange, colorHexMap }) {
  return (
    <div className="tile-grid">
      {options.map((c) => (
        <TileButton
          key={c}
          title={c}
          active={value === c}
          onClick={() => onChange(c)}
          style={
            colorHexMap?.[c] ? { background: colorHexMap[c] } : { background: `var(--bs-${c})` }
          }
        />
      ))}
    </div>
  );
}
function IconGridPicker({ value, onChange }: { value: string; onChange: (ic: string) => void }) {
  return (
    <>
      {Object.entries(ICON_GROUPS).map(([group, icons]) => (
        <div key={group} className="mb-2">
          <div className="text-muted small mb-1 text-uppercase">{group}</div>
          <div className="tile-grid">
            {icons.map((ic) => (
              <TileButton key={ic} active={value === ic} onClick={() => onChange(ic)} title={ic}>
                {/* ic already includes the FA style prefix, e.g. "fas fa-..." */}
                <i className={ic} />
              </TileButton>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

function TileButton({
  active = false,
  onClick,
  title,
  children,
  style,
}: {
  active?: boolean;
  onClick?: () => void;
  title?: string;
  children?: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`tile-btn ${active ? 'tile-active' : ''}`}
      style={style}
    >
      {children}
    </button>
  );
}

/* ───────── Main component ───────── */
export const FlowKanbanEditor = ({
  userFlow,
  initialFlowName = '',
  initialStages = [],
  colorOptions,
  colorHexMap,
  iconPrefix = 'far',
  onUpsert,
  onCancel,
  onDeleteFlow,
  mode,
  userScope,
}: FlowKanbanEditorProps) => {
  const [flowName, setFlowName] = React.useState(initialFlowName);
  const [isEditingName, setIsEditingName] = React.useState(false);
  const [loaAccess, setLoaAccess] = React.useState<boolean>(userFlow?.isLoaAccess() || false);
  // Track current working list
  const [stages, setStages] = React.useState<StageVM[]>(
    (initialStages ?? []).map((s, i) => ({
      ...s,
      _tmpId: s._tmpId || rid(),
      sortOrder: typeof s.sortOrder === 'number' ? s.sortOrder : i,
      color: s.color || colorOptions[0] || 'primary',
      icon: s.icon || ICONS_FLAT[0] || 'fa-flag',
    }))
  );

  // Track deleted stage ids so we can send them in the bundle
  const [deletedIds, setDeletedIds] = React.useState<number[]>([]);

  // For convenience: the ordered view
  const ordered = React.useMemo(
    () => [...stages].sort((a, b) => a.sortOrder - b.sortOrder),
    [stages]
  );

  const [dragCol, setDragCol] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  /* ───────── DnD + CRUD ───────── */
  const onColDragOver = (e: React.DragEvent) => e.preventDefault();
  const onColDragStart = (id: string) => setDragCol(id);
  const onColDrop = (targetId: string) => {
    if (!dragCol || dragCol === targetId) return;
    setStages((prev) => {
      const list = [...prev].sort((a, b) => a.sortOrder - b.sortOrder);
      const fromIdx = list.findIndex((s) => s._tmpId === dragCol);
      const toIdx = list.findIndex((s) => s._tmpId === targetId);
      if (fromIdx < 0 || toIdx < 0) return prev;
      const [moved] = list.splice(fromIdx, 1);
      list.splice(toIdx, 0, moved);
      return list.map((s, i) => ({ ...s, sortOrder: i }));
    });
    setDragCol(null);
  };

  const addStage = () => {
    const lastStage = stages[stages.length - 1];
    const lastColorIndex = !lastStage
      ? 0
      : colorOptions.findIndex((item) => item == lastStage.color);
    const newIndex = !lastStage
      ? 0
      : lastColorIndex + 1 > colorOptions.length
        ? 0
        : lastColorIndex + 1;

    const lastIconIndex = !lastStage ? 0 : ICONS_FLAT.findIndex((item) => item == lastStage.icon);
    const newIconIndex = !lastStage
      ? 0
      : lastIconIndex + 1 > ICONS_FLAT.length
        ? 0
        : lastIconIndex + 1;

    const baseStage = {
      _tmpId: rid(),
      name: '(Untitled)',
      color: colorOptions[newIndex] || 'primary',
      icon: ICONS_FLAT[newIconIndex] || 'fa-flag',
    };

    setStages((prev) => {
      const newStage = {
        ...baseStage,
        sortOrder: prev.length,
      };

      return [...prev, newStage];
    });
  };

  const removeStage = (_tmpId: string) => {
    setStages((prev) => {
      const target = prev.find((s) => s._tmpId === _tmpId);
      if (target?.id) {
        setDeletedIds((d) => (d.includes(target.id!) ? d : [...d, target.id!]));
      }
      const kept = prev.filter((s) => s._tmpId !== _tmpId);
      return kept.map((s, i) => ({ ...s, sortOrder: i }));
    });
  };

  const updateStage = (_tmpId: string, patch: Partial<StageVM>) =>
    setStages((prev) => prev.map((s) => (s._tmpId === _tmpId ? { ...s, ...patch } : s)));

  /* ───────── Save (single-transaction bundle) ───────── */
  const handleSave = async () => {
    setError(null);

    if (!flowName.trim()) return setError('Please enter a flow name.');

    const empty = stages.find((s) => !s.name?.trim());
    if (empty) return setError('Each stage must have a name.');

    // Normalize current order to 0..n just before save
    const normalized = [...stages]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((s, i) => ({ ...s, sortOrder: i }));

    // Build bundle
    const bundle: FlowBundlePayload = {
      flowId: userFlow?.getId(),
      flowName: flowName.trim(),
      loaAccess: loaAccess,
      stages: {
        upserts: normalized.map((s) => ({
          id: s.id,
          clientTmpId: s.id ? undefined : s._tmpId,
          name: s.name.trim(),
          color: s.color ?? null,
          icon: s.icon ?? null,
          sortOrder: s.sortOrder,
        })),
        deletes: deletedIds,
      },
    };

    try {
      setSaving(true);
      await onUpsert(bundle);
      setDeletedIds([]);
    } catch (e: any) {
      setError(e?.message || 'Failed to save flow.');
    } finally {
      setSaving(false);
    }
  };

  const handleMakeDuplicate = async (sourceFlow: UserFlow) => {
    const stages = sourceFlow
      .getFlowStages()
      .getItems()
      .sort((a, b) => (a.getSortOrder() ?? 0) - (b.getSortOrder() ?? 0))
      .map((s, i) => ({
        // no id; let server create new ones
        clientTmpId: `dup-${s.getId() ?? i}-${Date.now()}`,
        name: s.getName(),
        color: s.getColor() ?? null,
        icon: s.getIcon() ?? null,
        sortOrder: i, // normalized
      }));

    const flow = {
      // no flowId => create
      flowName: `${sourceFlow.getName()} (Copy)`,
      stages: {
        upserts: stages,
        deletes: [],
      },
    };

    await onUpsert(flow);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    onDeleteFlow(userFlow.getId());
  };

  /* ───────── UI (unchanged except remove button wires removeStage) ───────── */
  const swatchStyle = (c: string) =>
    colorHexMap?.[c] ? { background: colorHexMap[c] } : { background: `var(--bs-${c})` };

  // Build server stages from the latest userFlow prop
  const serverStages = React.useMemo(() => {
    const items = userFlow?.getFlowStages?.()?.getItems?.() ?? [];
    return items
      .slice()
      .sort((a, b) => (a.getSortOrder?.() ?? 0) - (b.getSortOrder?.() ?? 0))
      .map((s, i) => ({
        id: s.getId(), // <-- real id from server
        _tmpId: String(s.getId() ?? `srv-${i}`),
        name: s.getName(),
        color: s.getColor() ?? 'primary',
        icon: s.getIcon() ?? 'fa-flag',
        sortOrder: s.getSortOrder() ?? i,
      }));
  }, [userFlow]);

  // Signature changes when ids/names/colors/icons/order change
  const serverStageSignature = React.useMemo(
    () =>
      (serverStages || [])
        .map((s) => `${s.id}|${s.name}|${s.color}|${s.icon}|${s.sortOrder}`)
        .join(','),
    [serverStages]
  );

  // Whenever the server's version changes, replace local editor state
  React.useEffect(() => {
    if (!userFlow) return;
    setFlowName(userFlow.getName?.() ?? '');
    setLoaAccess(userFlow.isLoaAccess());
    setStages(serverStages); // <-- replace local with authoritative data
    setDeletedIds([]); // clear any local delete marks
  }, [userFlow?.getId?.(), serverStageSignature]);

  return (
    <>
      <div className="d-flex align-items-center mb-3 mt-3 ml-2">
        <i className="far fa-stream mr-2 text-primary" />

        {isEditingName ? (
          <input
            value={flowName}
            onChange={(e) => setFlowName(e.target.value)}
            onBlur={() => setIsEditingName(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
              if (e.key === 'Escape') {
                setIsEditingName(false);
                setFlowName(initialFlowName || '');
              }
            }}
            className="form-control-plaintext p-0 m-0 bg-transparent border-0 shadow-none d-inline-block w-auto flex-grow-0"
            style={{ outline: 'none' }}
            autoFocus
          />
        ) : (
          <h5 className="m-0 cp" onClick={() => setIsEditingName(true)}>
            {flowName || 'Untitled flow'}
          </h5>
        )}

        {/* Ellipsis menu */}
        {userFlow?.getId() && userScope?.userSubTypeDesignation !== 'community' && (
          <div className="dropdown d-inline-block mr-2 ml-auto">
            <div className="cp dropdown-toggle" data-toggle="dropdown" aria-expanded="false">
              <i className="far fa-ellipsis-h mr-1"></i>
            </div>

            <div className="dropdown-menu" style={{ fontSize: '9pt' }}>
              <div className="dropdown-item" onClick={() => handleMakeDuplicate(userFlow)}>
                <i className="far fa-copy mr-2"></i>Duplicate
              </div>
              {userScope?.hasLoaCommunityAgents && (
                <div className="dropdown-item" onClick={() => setLoaAccess(!loaAccess)}>
                  <i className="far fa-copy mr-2"></i>
                  {loaAccess ? <>Disable LOA access</> : <>Enable LOA access</>}
                </div>
              )}
              <div className="dropdown-divider"></div>
              <div
                className="dropdown-item font-weight-bold text-danger hideDelete"
                onClick={handleDeleteClick}
              >
                <i className="far fa-trash mr-2"></i>Delete
              </div>
            </div>
          </div>
        )}
      </div>
      {loaAccess && (
        <div className="badge badge-success mb-2 ml-2">
          <i className="far fa-check"></i> Sharing with LOA
        </div>
      )}
      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="w-100">
        <div className="d-flex flex-column">
          {ordered.map((s) => (
            <div
              key={s._tmpId}
              className="px-2 mb-2"
              style={{ width: '100%', flex: '0 0 auto' }}
              draggable
              onDragStart={() => onColDragStart(s._tmpId)}
              onDragOver={onColDragOver}
              onDrop={() => onColDrop(s._tmpId)}
              onDragEnd={() => setDragCol(null)}
            >
              <div
                className={`card border border-${s.color} p-0`}
                style={{ width: '100%', height: 'auto' }}
              >
                <div className="card-header proCrdTitle d-flex align-items-center">
                  <i className={`${iconPrefix} ${s.icon} mr-2 text-${s.color}`} />
                  <input
                    value={s.name}
                    onChange={(e) => updateStage(s._tmpId, { name: e.target.value })}
                    className="p-0 bg-transparent border-0 shadow-none d-inline-block w-auto flex-grow-1 font-weight-bold"
                    style={{ outline: 'none' }}
                  />

                  {/* COLOR */}
                  <Popout
                    align="right"
                    trigger={() => (
                      <TileButton
                        title="Pick color"
                        style={{ marginLeft: 8, ...swatchStyle(s.color) }}
                      >
                        <i
                          className={`${iconPrefix} ${s.icon}`}
                          style={{ opacity: 0 }}
                          aria-hidden
                        />
                      </TileButton>
                    )}
                  >
                    <ColorSwatchPicker
                      value={s.color}
                      options={colorOptions}
                      onChange={(c) => updateStage(s._tmpId, { color: c })}
                      colorHexMap={colorHexMap}
                    />
                  </Popout>

                  {/* ICON */}
                  <Popout
                    align="right"
                    trigger={() => (
                      <TileButton title="Pick icon" style={{ marginLeft: 8 }}>
                        <i className={`${iconPrefix} ${s.icon} text-${s.color}`} />
                      </TileButton>
                    )}
                  >
                    <IconGridPicker
                      value={s.icon}
                      onChange={(ic) => updateStage(s._tmpId, { icon: ic })}
                    />
                  </Popout>

                  <button
                    className="btn btn-sm tile-btn text-danger ml-2 hideDelete"
                    onClick={() => removeStage(s._tmpId)}
                  >
                    <i className="far fa-trash" />
                  </button>
                </div>

                <div className="card-body" />
              </div>
            </div>
          ))}
          <div
            className="btn btn-sm border border-primary text-primary p-2 d-inline-block cp px-3 mx-2"
            style={{ border: 'none', borderRadius: '15px' }}
            onClick={addStage}
          >
            <i className="far fa-plus mr-1" /> Add Stage
          </div>
          {deletedIds.length > 0 && (
            <div className="mt-2">
              <div className="alert alert-danger p-1" style={{ fontSize: '9pt' }}>
                <i className="far fa-exclamation-circle mr-1"></i> Deleting a stage will also remove
                individuals from that stage.
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="d-flex mt-3">
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? (
            <i className="far fa-spinner fa-spin mr-2" />
          ) : (
            <i className="far fa-save mr-2" />
          )}{' '}
          Save Flow
        </button>
        <button className="btn btn-link ml-2" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </>
  );
};
